import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ContentLibrary.css";
import Header from "./../../Components/Header/Header";
import DeleteIcon from "../../assets/images/DeleteIcon.png";
import RejectedIcon from "../../assets/images/RejectedIcon.png";
import ContentLib_Delete_Icon from "../../assets/images/ContentLib_Delete_Icon.svg";
import ContentLib_Insights_Icon from "../../assets/images/ContentLib_Insights_Icon.svg";
import PaginationLeft from "../../assets/images/pagination-left-arrow.svg";
import PaginationRight from "../../assets/images/pagination-right-arrow.svg";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import {
  formatContentDate,
  formatDate,
  formatDuration,
  formatDurationSeconds,
} from "../../utils/helpers";
import { showToast } from "../../utils/toastUtils";
import AwaitingApprovalIcon from "../../assets/images/Review.svg";


const ContentLibrary = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Status");

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const ITEMS_PER_PAGE = 10;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const tabs = ["ALL", "Video", "Reel", "Story"];
  const [contentData, setContentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchContentLibrary = async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        type: activeTab,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        ...(searchQuery?.trim() && { search: searchQuery.trim() }),
        adminApprovalStatus:
          filterStatus === "All Status"
            ? "ALL"
            : filterStatus === "Admin Verified"
              ? "APPROVED"
              : filterStatus === "Pending"
                ? "PENDING"
                : "REJECTED",
        publishStatus: "ALL",
      });

      const response = await callApi(
        `${videoCreatorEndPoint}contentDashboard/list?${queryParams}`,
        "GET",
      );

      if (response?.status) {
        setContentData(response?.data?.content || []);
        setTotalItems(response?.data?.totalItems || 0);
        setTotalPages(response?.data?.totalPages || 0);
      }
    } catch (error) {
      console.error("Content Library API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);

      const response = await callApi(
        `${videoCreatorEndPoint}${selectedItem?.contentType}/${selectedItem?.id}`,
        "DELETE",
      );

      if (response?.status) {
        showToast(
          "success",
          response?.alert || "Content deleted successfully",
          100,
        );

        // Refresh list
        fetchContentLibrary();
      }
    } catch (error) {
      showToast(
        "error",
        error?.message || "Something went wrong while deleting content",
      );
    } finally {
      handleCloseDeleteModal();
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchContentLibrary();
  }, [activeTab, filterStatus, currentPage, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, filterStatus]);

  // Filter data based on active tab, search query, and status
  const filteredData = useMemo(() => {
    return contentData.filter((item) => {
      const title = item.title || item.caption || item.description || "";

      return title.toLowerCase().includes(searchQuery.toLowerCase().trim());
    });
  }, [contentData, searchQuery]);

  const getTotalCount = () => contentData.length;
  const showingFrom =
    totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const showingTo = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleInsightsClick = (item) => {
    if (item.adminApprovalStatus.toLocaleLowerCase() === "rejected") {
      setSelectedItem(item);
      setShowRejectModal(true);
    }
    if (item.adminApprovalStatus.toLocaleLowerCase() === "pending") {
      setSelectedItem(item);
      setShowWaitingModal(true);
    }
    if (
      item.adminApprovalStatus.toLocaleLowerCase() === "approved" &&
      ["Video", "Reel", "Story"].includes(item.contentType)
    ) {
      navigate("/video-insights", { state: { item } });
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedItem(null);
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setSelectedItem(null);
  };

  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1,
  );

  return (
    <>
      <div className="dashboard">
        <div className="dashboard-bg-stripe"></div>
        <Header />
        <div className="content-library-container">
          {/* Header and Controls - same as before */}
          <div className="content-library-header">
            <div className="header-top">
              <h1 className="library-title">Content Library</h1>
              <p className="library-subtitle">
                Manage your content ({totalItems} total items)
              </p>
            </div>
          </div>

          <div className="library-controls">
            <div className="tabs-wrapper">
              {tabs.map((tab) => {
                return (
                  <button
                    key={tab}
                    className={`content-lib-tab-button ${activeTab === tab ? "active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>

            <div className="controls-right">
              <div className="search-box">
                <svg
                  className="search-icon"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search by Content….."
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="status-dropdown">
                <select
                  className="status-select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option>All Status</option>
                  <option>Pending</option>
                  <option>Admin Verified</option>
                  <option>Admin Rejected</option>
                </select>
                <svg
                  className="dropdown-icon"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="content-lib-table">
              <thead>
                <tr>
                  <th>Content</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Published Date/Time</th>
                  <th>Verified Status</th>
                  <th>Admin</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <tr key={item.id}>
                      <td className="content-cell">
                        <div className="content-item">
                          <div style={{ width: "139px" }}>
                            <img
                              src={
                                item?.contentType === "Video"
                                  ? item.thumbnailImage
                                  : item?.contentType === "Story"
                                    ? item?.thumbnailImage === ""
                                      ? item?.storyUrl
                                      : item?.thumbnailImage
                                    : item?.generatedThumbnail
                              }
                              alt={item.title}
                              className={`${item?.contentType.toLocaleLowerCase() === "video" ? "thumbnail-video" : "thumbnail-reel-story"}`}
                            />
                          </div>

                          <span className="content-lib-title">
                            {item.title || item.caption || "-"}
                          </span>
                        </div>
                      </td>
                      <td>{item?.contentType}</td>

                      <td>{`${item?.contentType === "Video" ? formatDurationSeconds(item.videoDuration) : item?.contentType === "Story" ? formatDurationSeconds(item?.storyDuration) : formatDurationSeconds(item?.reelDuration)}`}</td>
                      <td>
                        <div className="date-time">
                          <div>{formatDate(item.createdAt)}</div>
                          <div className="time">
                            {formatContentDate(item.createdAt)}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`status-badge status-${item?.adminApprovalStatus.toLocaleLowerCase()}`}
                        >
                          {item?.adminApprovalStatus.toLocaleLowerCase() ==
                          "approved" ? (
                            <>
                              <span style={{ paddingLeft: "5px" }}>Admin </span>{" "}
                              <br /> <span>Verified</span>
                            </>
                          ) : item?.adminApprovalStatus.toLocaleLowerCase() ==
                            "pending" ? (
                            <>
                              <span style={{ paddingLeft: "5px" }}>
                                Pending{" "}
                              </span>{" "}
                              <br />
                            </>
                          ) : item?.adminApprovalStatus.toLocaleLowerCase() ==
                            "rejected" ? (
                            <>
                              <span style={{ paddingLeft: "5px" }}>Admin </span>{" "}
                              <br /> <span>Rejected</span>
                            </>
                          ) : (
                            <span style={{ textAlign: "end" }}> - </span>
                          )}
                        </span>
                      </td>
                      <td>
                        <div className="admin-actions">
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteClick(item)}
                          >
                            <div style={{ display: "flex", gap: "10px" }}>
                              <img
                                src={ContentLib_Delete_Icon}
                                alt="ContentLib_Delete_Icon"
                                style={{ width: "20px", height: "20px" }}
                              />
                              <p className="action-btn-text"> Delete</p>
                            </div>
                          </button>
                          <button
                            className="action-btn insights-btn"
                            onClick={() => handleInsightsClick(item)}
                          >
                            <div style={{ display: "flex", gap: "10px" }}>
                              <img
                                src={ContentLib_Insights_Icon}
                                alt="ContentLib_Insights_Icon"
                                style={{ width: "20px", height: "20px" }}
                              />
                              <p className="action-btn-text"> Insights</p>
                            </div>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#6b7280",
                      }}
                    >
                      No content found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="pagination-wrapper">
              <span className="content-pagination-info">
                Showing
                <span className="content-pagination-count">
                  {" "}
                  {showingFrom}-{showingTo}
                </span>{" "}
                from
                <span className="content-pagination-count">
                  {" "}
                  {totalItems}
                </span>{" "}
                data
              </span>
              <div className="pagination-controls">
                <img
                  src={PaginationLeft}
                  alt="Left Arrow"
                  style={{
                    width: "32px",
                    height: "32px",
                    cursor: currentPage > 1 ? "pointer" : "not-allowed",
                    opacity: currentPage > 1 ? 1 : 0.5,
                  }}
                  onClick={() =>
                    currentPage > 1 && setCurrentPage((prev) => prev - 1)
                  }
                />

                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    className={`content-pagination-btn page-btn ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}

                <img
                  src={PaginationRight}
                  alt="Right Arrow"
                  style={{
                    width: "32px",
                    height: "32px",
                    cursor:
                      currentPage < totalPages ? "pointer" : "not-allowed",
                    opacity: currentPage < totalPages ? 1 : 0.5,
                  }}
                  onClick={() =>
                    currentPage < totalPages &&
                    setCurrentPage((prev) => prev + 1)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <img src={DeleteIcon} alt="delete" />
              <h3 className="">Are you sure you want to delete?</h3>
            </div>
            <p>
              Do you really want to delete this record? Once deleted, you won’t
              be able to view it in your list anymore.
            </p>
            <div className="modal-buttons">
              <button
                onClick={handleDeleteConfirm}
                className="modal-btn confirm-btn"
              >
                Yes, Delete it.
              </button>
              <button
                className="modal-btn keep-btn"
                onClick={handleCloseDeleteModal}
              >
                No, Keep it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Not Approved Modal */}
      {showRejectModal && selectedItem && (
        <div className="modal-overlay" onClick={handleCloseRejectModal}>
          <div className="reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <img src={RejectedIcon} alt="rejected" />
              <h3>Content Not Approved</h3>
            </div>
            <p>
              Your content was not approved by the admin. Please review the
              reason below, make the required changes, and resubmit the video
              for approval.
            </p>
            <div className="reject-reason">
              <strong>Rejection Reason:</strong> Promotional content is not
              allowed.
            </div>
            <div className="modal-buttons">
              <button
                className="modal-btn keep-btn"
                onClick={handleCloseRejectModal}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

            {showWaitingModal && (
              <div className="caa-modal-overlay">
                <div className="caa-modal-content caa-publish-modal">
                  <img
                    src={AwaitingApprovalIcon}
                    alt="awaiting-approval"
                    className="caa-success-icon"
                  />
                  <h2 className="caa-modal-title">Content Under Review</h2>
                  <p className="caa-modal-message">
                    Your content is currently under review. Insights will be available once it is approved.
                  </p>
                  <button
                    className="caa-modal-button caa-okay-button"
                    onClick={() => setShowWaitingModal(false)}
                  >
                    Okay
                  </button>
                </div>
              </div>
            )}
    </>
  );
};

export default ContentLibrary;
